import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { Building2, FilePlus2, RefreshCw } from 'lucide-react'
import { Modal } from '../components/Modal'
import { MetropolitanBatchReviewModal } from '../components/MetropolitanBatchReviewModal'
import { useAuth } from '../lib/AuthContext'
import { brazilianDecimal, compareMetropolitanUnitCodes, cubQuantity, databaseDecimal, digits, exceptionalBoletoAmount, metropolitanChargeLabels, monthlyItems, type MetropolitanItemType, validCpfCnpj } from '../lib/metropolitan'
import { loadMetropolitanBatchReview, loadMetropolitanSchedule, prepareMetropolitanBankBatch, previewCub, type MetropolitanBatchReview, type MetropolitanScheduleItem } from '../lib/metropolitanData'
import { formatMoney } from '../lib/format'
import { supabase } from '../lib/supabase'

const current = `${format(new Date(), 'yyyy-MM')}-01`
type Draft = Record<string, string>
const blank: Draft = {
  name: '',
  document: '',
  email: '',
  phone: '',
  default_due_day: '5',
  zip_code: '',
  address: '',
  address_number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: 'PR',
  enterprise: 'AN Metropolitan',
  unit_code: '',
  contract_date: format(new Date(), 'yyyy-MM-dd'),
  reference_cub_competence: current,
  reference_cub: '',
  due_day: '5',
  first_due_date: format(new Date(), 'yyyy-MM-dd'),
  installment_total: '1',
  installment_base: '',
}

export function MetropolitanSchedulePage() {
  const { profile } = useAuth()
  const admin = profile?.role === 'admin'
  const [competence, setCompetence] = useState(current),
    [items, setItems] = useState<MetropolitanScheduleItem[]>([]),
    [loading, setLoading] = useState(true),
    [notice, setNotice] = useState(''),
    [open, setOpen] = useState(false)
  const [cub, setCub] = useState(''),
    [preview, setPreview] = useState<{ item_id: string; old_amount: string | null; new_amount: string }[]>([]),
    [selected, setSelected] = useState<Set<string>>(new Set()),
    [query, setQuery] = useState(''),
    [type, setType] = useState<'TODOS' | MetropolitanItemType>('TODOS'),
    [preparing, setPreparing] = useState(false)
  const [batchReview, setBatchReview] = useState<MetropolitanBatchReview | null>(null),
    [overrideDueDate, setOverrideDueDate] = useState(''),
    [overrideAmount, setOverrideAmount] = useState('')
  async function refresh() {
    setLoading(true)
    try {
      setItems(await loadMetropolitanSchedule(competence))
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Não foi possível carregar o cronograma.')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    setSelected(new Set())
    setPreview([])
    setOverrideDueDate('')
    setOverrideAmount('')
    refresh()
  }, [competence])
  const visible = useMemo(() => items
    .filter((i) => (type === 'TODOS' || i.item_type === type) && `${i.contract.customer.name} ${i.contract.unit.code}`.toLowerCase().includes(query.toLowerCase()))
    .sort((left, right) => compareMetropolitanUnitCodes(left.contract.unit.code, right.contract.unit.code)
      || left.due_date.localeCompare(right.due_date)
      || left.item_type.localeCompare(right.item_type)
      || left.item_number - right.item_number), [items, type, query])
  const chosen = items.filter((i) => selected.has(i.id)),
    total = chosen.length === 1 && overrideAmount.trim()
      ? (() => { try { return Number(exceptionalBoletoAmount(overrideAmount)) } catch { return 0 } })()
      : chosen.reduce((sum, i) => sum + Number(i.updated_amount || 0), 0)
  async function showPreview() {
    try {
      const amount = databaseDecimal(cub)
      if (!/^\d+(?:\.\d+)?$/.test(amount) || Number(amount) <= 0) throw new Error('Informe um valor válido para o CUB.')
      setPreview(await previewCub(competence, amount))
      setNotice('Prévia gerada. Confirme a atualização para gravar o CUB e recalcular itens elegíveis.')
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'CUB inválido.')
    }
  }
  async function confirmCub() {
    try {
      const amount = databaseDecimal(cub)
      if (!/^\d+(?:\.\d+)?$/.test(amount) || Number(amount) <= 0) throw new Error('Informe um valor válido para o CUB.')
      await previewCub(competence, amount, true)
      await refresh()
      setPreview([])
      setNotice('CUB confirmado. Nenhum boleto foi emitido.')
    } catch (e) {
      setNotice(e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Não foi possível confirmar o CUB.')
    }
  }
  async function prepareBatch() {
    setPreparing(true)
    try {
      const amount = overrideAmount.trim() ? exceptionalBoletoAmount(overrideAmount) : undefined
      if (amount && chosen.length !== 1) throw new Error('O valor excepcional só pode ser usado para um único boleto.')
      const batchId = await prepareMetropolitanBankBatch(
        competence, [...selected], chosen.length === 1 ? overrideDueDate : undefined, amount,
      )
      const review = await loadMetropolitanBatchReview(batchId)
      setBatchReview(review)
      setNotice(`Lote ${batchId.slice(0, 8)} preparado para revisão. Nenhum boleto foi emitido.`)
      await refresh()
    } catch (e) {
      const message = e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' ? e.message : 'Não foi possível preparar o lote.'
      setNotice(message)
    } finally {
      setPreparing(false)
    }
  }
  function toggle(id: string) {
    setOverrideDueDate('')
    setOverrideAmount('')
    setSelected((s) => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }
  return (
    <section className="page metropolitan-page">
      <div className="page-intro">
        <div>
          <span className="eyebrow">AN Metropolitan</span>
          <h1>Cronograma de Clientes</h1>
          <p>Parcelas e balões são itens independentes e nunca são unificados.</p>
        </div>
        {admin && (
          <div className="page-actions">
            <button onClick={() => setOpen(true)}>
              <FilePlus2 size={16} />
              Adicionar cliente/unidade
            </button>
          </div>
        )}
      </div>
      {notice && <Banner text={notice} />}
      <div className="card-toolbar metropolitan-toolbar">
        <label>
          Competência
          <input type="month" value={competence.slice(0, 7)} onChange={(e) => setCompetence(`${e.target.value}-01`)} />
        </label>
        <label>
          Pesquisar
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cliente ou unidade" />
        </label>
        <label>
          Tipo
          <select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <option value="TODOS">Todos</option>
            <option value="PARCELA">Parcelas</option>
            <option value="BALAO">Balões</option>
          </select>
        </label>
        <button className="secondary" onClick={refresh}>
          <RefreshCw size={15} />
          Atualizar
        </button>
      </div>
      {admin && (
        <div className="form-card cub-card">
          <div>
            <span className="eyebrow">Atualização mensal</span>
            <h2>CUB/PR vigente</h2>
            <p>Quantidade fixa de CUBs × CUB vigente.</p>
          </div>
          <label>
            Valor do CUB
            <input value={cub} onChange={(e) => { setCub(e.target.value); setPreview([]) }} onBlur={() => { if (cub.trim()) setCub(brazilianDecimal(cub)) }} inputMode="decimal" placeholder="0,00" />
          </label>
          <button className="secondary" disabled={!cub} onClick={showPreview}>
            Ver prévia
          </button>
          <button disabled={!cub || !preview.length} onClick={confirmCub}>
            Confirmar atualização
          </button>
        </div>
      )}
      {preview.length > 0 && <Banner text={`Prévia de ${preview.length} itens. Boletos já emitidos não serão alterados.`} />}
      <div className="selection-summary">
        <span>
          <strong>{chosen.length}</strong> itens selecionados
        </span>
        <span>
          <strong>{chosen.filter((i) => i.item_type === 'PARCELA').length}</strong> parcelas
        </span>
        <span>
          <strong>{chosen.filter((i) => i.item_type === 'BALAO').length}</strong> balões
        </span>
        <span>
          <strong>{chosen.length}</strong> boletos separados
        </span>
        <span className="money">{formatMoney(total)}</span>
        {chosen.length === 1 && (
          <>
            <label className="single-due-date">
              Vencimento excepcional
              <input type="date" value={overrideDueDate} min={format(new Date(Date.now() + 86400000), 'yyyy-MM-dd')} onChange={(e) => setOverrideDueDate(e.target.value)} />
              <small>Opcional; em branco usa o dia {chosen[0].contract.customer.default_due_day || 5}.</small>
            </label>
            <label className="single-due-date">
              Valor excepcional
              <input
                value={overrideAmount}
                onChange={(e) => setOverrideAmount(e.target.value)}
                onBlur={() => {
                  if (!overrideAmount.trim()) return
                  try { setOverrideAmount(exceptionalBoletoAmount(overrideAmount).replace('.', ',')) } catch { /* Shown when preparing the lot. */ }
                }}
                inputMode="decimal"
                placeholder="0,00"
              />
              <small>Opcional; valor final do boleto, sem correção pelo CUB. Não altera o cronograma.</small>
            </label>
          </>
        )}
        <button className="secondary small" onClick={() => { setSelected(new Set(visible.map((i) => i.id))); setOverrideDueDate(''); setOverrideAmount('') }}>
          Selecionar todos
        </button>
        <button
          className="secondary small"
          onClick={() => {
            setSelected(new Set())
            setOverrideDueDate('')
            setOverrideAmount('')
          }}
        >
          Desmarcar todos
        </button>
        {admin && (
          <button disabled={!chosen.length || preparing} onClick={() => void prepareBatch()}>
            {preparing ? 'Preparando…' : 'Preparar lote'}
          </button>
        )}
      </div>
      <div className="data-card table-scroll">
        <table className="data-table metropolitan-table">
          <thead>
            <tr>
              <th>Seleção</th>
              <th>Cliente</th>
              <th>Unidade</th>
              <th>Tipo</th>
              <th>Nº</th>
              <th>CUB ref.</th>
              <th>Qtd. CUBs</th>
              <th>CUB vigente</th>
              <th>Valor-base</th>
              <th>Atualizado</th>
              <th>Vencimento</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="empty">
                  Carregando…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={12} className="empty">
                  Nenhum item nesta competência.
                </td>
              </tr>
            ) : (
              visible.map((i) => (
                <tr key={i.id}>
                  <td>
                    <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} aria-label={`Selecionar ${i.contract.customer.name}`} />
                  </td>
                  <td>
                    <strong>{i.contract.customer.name}</strong>
                  </td>
                  <td>{i.contract.unit.code}</td>
                  <td>
                    <span className={`status ${i.item_type === 'BALAO' ? 'pending' : 'active'}`}>{i.item_type === 'BALAO' ? 'Balão' : 'Parcela'}</span>
                  </td>
                  <td>{i.display_fraction}</td>
                  <td>{i.reference_cub}</td>
                  <td>{i.cub_quantity}</td>
                  <td>{i.applied_cub ?? '—'}</td>
                  <td className="money">{formatMoney(Number(i.base_amount))}</td>
                  <td className="money">{i.updated_amount ? formatMoney(Number(i.updated_amount)) : '—'}</td>
                  <td>{i.due_date.split('-').reverse().join('/')}</td>
                  <td>
                    <span className="status pending">{i.status}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Banner text={chosen.length ? `${chosen.length} cobranças preparadas para revisão. A emissão exige confirmação explícita.` : 'Selecione parcela, balão ou ambos. “Ambos” sempre gera dois boletos separados.'} />
      {batchReview && (
        <MetropolitanBatchReviewModal
          batch={batchReview}
          onClose={() => setBatchReview(null)}
          onConfirmed={() => {
            setBatchReview(null)
            setSelected(new Set())
            setNotice('Emissão confirmada. O lote aguarda o adaptador local; nenhum boleto foi criado pelo navegador.')
            void refresh()
          }}
        />
      )}
      {open && (
        <Wizard
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            refresh()
            setNotice('Cadastro e cronograma criados em uma única transação.')
          }}
        />
      )}
    </section>
  )
}
function Banner({ text }: { text: string }) {
  return (
    <div className="info-banner">
      <Building2 size={19} />
      <div>
        <strong>Metropolitan</strong>
        <span>{text}</span>
      </div>
    </div>
  )
}
function Wizard({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [step, setStep] = useState(1),
    [d, setD] = useState<Draft>(blank),
    [balloons, setBalloons] = useState<{ due_date: string; base_amount: string }[]>([]),
    [error, setError] = useState(''),
    [saving, setSaving] = useState(false)
  const change = (k: string, v: string) => setD((x) => ({ ...x, [k]: v }))
  const installments = useMemo(() => (d.reference_cub && d.installment_base && d.unit_code ? monthlyItems(d.first_due_date, Number(d.default_due_day), Number(d.installment_total), d.installment_base, d.reference_cub, d.unit_code) : []), [d])
  const rows = [
    ...installments,
    ...balloons
      .filter((b) => b.due_date && b.base_amount)
      .map((b, n) => ({
        competence: `${b.due_date.slice(0, 7)}-01`,
        item_type: 'BALAO' as const,
        item_number: n + 1,
        item_total: balloons.length,
        display_fraction: `${n + 1}/${balloons.length}`,
        base_amount: b.base_amount,
        cub_quantity: cubQuantity(b.base_amount, d.reference_cub),
        due_date: b.due_date,
        description: metropolitanChargeLabels(d.unit_code, 'BALAO', n + 1, balloons.length).description,
      })),
  ]
  function next() {
    if (step === 1 && (!validCpfCnpj(d.document) || Number(d.default_due_day) < 1 || Number(d.default_due_day) > 31)) return setError('Informe CPF/CNPJ válido e dia de vencimento entre 1 e 31.')
    if (step === 3 && (!d.reference_cub || Number(databaseDecimal(d.reference_cub)) <= 0)) return setError('Informe CUB de referência válido.')
    setError('')
    setStep((s) => Math.min(6, s + 1))
  }
  async function submit() {
    setSaving(true)
    try {
      const { error: e } = await supabase.rpc('metropolitan_create_contract_with_schedule', {
        p_customer: { ...d, document: digits(d.document) },
        p_unit: {
          enterprise: d.enterprise,
          code: d.unit_code,
          status: 'VENDIDA',
        },
        p_contract: {
          contract_date: d.contract_date,
          reference_cub: databaseDecimal(d.reference_cub),
          reference_cub_competence: d.reference_cub_competence,
          due_day: d.default_due_day,
          first_due_date: d.first_due_date,
          installment_total: d.installment_total,
          balloon_total: balloons.length,
        },
        p_items: rows.map((item) => ({
          ...item,
          base_amount: databaseDecimal(item.base_amount),
          cub_quantity: databaseDecimal(item.cub_quantity),
        })),
      })
      if (e) throw e
      onSaved()
    } catch (e) {
      const message = e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' ? e.message : 'Falha ao salvar.'
      setError(message)
    } finally {
      setSaving(false)
    }
  }
  return (
    <Modal title="Adicionar cliente/unidade" onClose={onClose} width="900px">
      <div className="wizard-steps">
        {['Cliente', 'Unidade', 'CUB', 'Parcelas', 'Balões', 'Revisão'].map((x, n) => (
          <span className={step === n + 1 ? 'active' : ''} key={x}>
            {n + 1}. {x}
          </span>
        ))}
      </div>
      {step === 1 && <div className="form-grid">{fields(['Nome ou razão social:name', 'CPF ou CNPJ:document', 'E-mail:email', 'Telefone:phone', 'Dia padrão de vencimento:default_due_day:number', 'CEP:zip_code', 'Endereço:address', 'Número:address_number', 'Complemento:complement', 'Bairro:neighborhood', 'Cidade:city', 'Estado:state'], d, change)}</div>}
      {step === 2 && <div className="form-grid">{fields(['Empreendimento:enterprise', 'Código da unidade:unit_code'], d, change)}</div>}
      {step === 3 && <div className="form-grid">{fields(['Data do contrato:contract_date:date', 'Competência CUB:reference_cub_competence:date', 'CUB de referência:reference_cub', 'Primeiro vencimento:first_due_date:date'], d, change)}</div>}
      {step === 4 && (
        <div className="form-grid">
          {fields(['Quantidade de parcelas:installment_total:number', 'Valor-base:installment_base'], d, change)}
          <p className="form-message span-2">Quantidade fixa: {d.reference_cub && d.installment_base ? cubQuantity(d.installment_base, d.reference_cub) : '—'} CUBs por parcela.</p>
        </div>
      )}
      {step === 5 && (
        <div className="stack">
          <button
            type="button"
            className="secondary"
            onClick={() =>
              setBalloons((x) => [
                ...x,
                {
                  due_date: d.first_due_date,
                  base_amount: '',
                },
              ])
            }
          >
            Adicionar balão
          </button>
          {balloons.map((b, n) => (
            <div className="form-grid" key={n}>
              {fields(['Vencimento:due_date:date', 'Valor-base:base_amount'], b, (k, v) => setBalloons((x) => x.map((r, i) => (i === n ? { ...r, [k]: v } : r))))}
            </div>
          ))}
        </div>
      )}
      {step === 6 && (
        <div className="stack">
          <p>
            <strong>{d.name}</strong> · Unidade {d.unit_code}
          </p>
          <p className="muted">
            {installments.length} parcelas e {balloons.length} balões independentes.
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Fração</th>
                  <th>Base</th>
                  <th>Qtd. CUBs</th>
                  <th>Vencimento</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={`${i.item_type}${i.item_number}`}>
                    <td>{i.item_type}</td>
                    <td>{i.display_fraction}</td>
                    <td>{i.base_amount}</td>
                    <td>{i.cub_quantity}</td>
                    <td>{i.due_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {error && <p className="form-message">{error}</p>}
      <div className="form-actions">
        <button type="button" className="secondary" onClick={step === 1 ? onClose : () => setStep((s) => s - 1)}>
          {step === 1 ? 'Cancelar' : 'Voltar'}
        </button>
        {step < 6 ? (
          <button type="button" onClick={next}>
            Avançar
          </button>
        ) : (
          <button type="button" disabled={saving} onClick={submit}>
            {saving ? 'Salvando…' : 'Confirmar cadastro'}
          </button>
        )}
      </div>
    </Modal>
  )
}
function fields(list: string[], values: Record<string, string>, change: (k: string, v: string) => void) {
  return list.map((row) => {
    const [label, key, type = 'text'] = row.split(':')
    return (
      <label key={key}>
        {label}
        <input type={type} value={values[key] || ''} onChange={(e) => change(key, e.target.value)} />
      </label>
    )
  })
}
